/**
 * CompetitorsTab - Displays and allows selection of product competitors
 * Stateless component that receives selection state from parent
 */

import { memo } from 'react';
import { useProductCompetitors } from '../../../hooks/useCompanies';
import type { CompetitorReference } from '../../../types';
import { relationshipBorderColors, relationshipBadgeColors } from './constants';

interface CompetitorsTabProps {
  productId: string;
  productName: string;
  existingCompetitors: CompetitorReference[];
  onSelectCompetitor: (id: string) => void;
  selectedCompetitorId: string | null;
}

function CompetitorsTabComponent({
  productId,
  productName,
  existingCompetitors,
  onSelectCompetitor,
  selectedCompetitorId,
}: CompetitorsTabProps) {
  const { data: competitorsData, isLoading } = useProductCompetitors(productId, 5);

  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-24 bg-gray-200 rounded-lg" />
        ))}
      </div>
    );
  }

  // Combine API competitors with existing profile competitors
  const apiCompetitors = competitorsData?.competitors || [];
  const allCompetitors = [
    ...apiCompetitors.map((c) => ({
      id: c.id,
      name: c.name,
      company_name: c.company_name,
      relationship: c.relationship,
      market_share: c.market_share,
      notes: c.notes,
      isFromApi: true,
    })),
    ...existingCompetitors.map((c, idx) => ({
      id: `existing-${idx}`,
      name: c.name,
      company_name: c.company_name,
      relationship: c.relationship,
      market_share: undefined,
      notes: c.notes,
      isFromApi: false,
    })),
  ];

  // Deduplicate by name
  const uniqueCompetitors = allCompetitors.filter(
    (comp, idx, arr) => arr.findIndex((c) => c.name === comp.name) === idx
  );

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Select a competitor to compare with <strong>{productName}</strong>
      </p>

      <div className="grid gap-3">
        {uniqueCompetitors.map((competitor) => {
          const isSelected = selectedCompetitorId === competitor.id;

          return (
            <button
              key={competitor.id}
              onClick={() => onSelectCompetitor(competitor.id)}
              className={`w-full text-left rounded-lg border-2 p-4 transition-all ${
                relationshipBorderColors[competitor.relationship]
              } ${isSelected ? 'ring-2 ring-primary-500 bg-primary-50' : 'bg-white hover:bg-gray-50'}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-medium text-gray-900">{competitor.name}</div>
                  {competitor.company_name && competitor.company_name !== competitor.name && (
                    <div className="text-sm text-gray-500">{competitor.company_name}</div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {competitor.market_share !== undefined && (
                    <span className="text-sm text-gray-600">{competitor.market_share}% share</span>
                  )}
                  <span
                    className={`text-xs px-2 py-0.5 rounded ${relationshipBadgeColors[competitor.relationship]}`}
                  >
                    {competitor.relationship}
                  </span>
                </div>
              </div>
              {competitor.notes && <p className="mt-2 text-sm text-gray-600">{competitor.notes}</p>}
            </button>
          );
        })}
      </div>

      {uniqueCompetitors.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No competitors identified for this product.
        </div>
      )}
    </div>
  );
}

export const CompetitorsTab = memo(CompetitorsTabComponent);
